"use client";

import Link from "next/link";
import { type MouseEvent, type ReactNode, useState } from "react";

import { cn } from "../../utils/cn.util";
import { DropdownMenu } from "../dropdown-menu";
import { CopyIcon, ExternalLinkIcon, StarFillIcon } from "../icons";
import type { SvgIcon } from "../icons/type";
import { Tooltip } from "../tooltip";
import { MOBILE_WIDTH, useLayoutContext } from "./context";
import SidebarItemLabel from "./SidebarItemLabel";

export interface LayoutSidebarBodyCategoryItemProps {
  name: string;
  icon?: SvgIcon;
  route?: string;
  isActive?: boolean;
  number?: ReactNode;
  className?: string;
  isFavorite?: boolean;
  favoriteOptionsData?: {
    name: string;
    icon?: SvgIcon;
    handler?: () => void;
  }[];
  onFavoriteChange?: (active: boolean) => void;
  onCopyLink?: () => void;
  onOpenNewTab?: () => void;
  disabledFavorite?: boolean;
  isExpandable?: boolean;
}

const SidebarBodyCategoryItem = ({
  className,
  name,
  icon: Icon,
  route,
  isActive,
  isFavorite,
  favoriteOptionsData,
  onFavoriteChange,
  onCopyLink,
  onOpenNewTab,
  number,
  disabledFavorite = true,
}: LayoutSidebarBodyCategoryItemProps) => {
  const [favoriteDropdownOpen, setFavoriteDropdownOpen] = useState<boolean>(false);
  const { openSidebar, setOpenSidebar } = useLayoutContext();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    if (window?.innerWidth <= MOBILE_WIDTH) {
      setOpenSidebar?.(false);
    }
  };

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <Link
          className={cn(
            "group group/item flex h-10 items-center justify-between rounded-lg py-3 pr-2.5 pl-3 text-neutral-22 transition-all hover:bg-primary-98 hover:text-primary-40",
            isActive && "bg-primary-95! text-primary-40",
            !openSidebar && "pl-2.5",
            className
          )}
          href={route || "/"}
          onClick={handleClick}
        >
          <SidebarItemLabel Icon={Icon} name={name} number={number} isActive={isActive} />
          {!disabledFavorite && (
            <DropdownMenu.Root open={favoriteDropdownOpen} onOpenChange={setFavoriteDropdownOpen}>
              <DropdownMenu.Trigger
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFavoriteDropdownOpen(false);
                }}
              >
                <StarFillIcon
                  className={cn(
                    "h-4 w-4 text-neutral-87 opacity-0 hover:opacity-100",
                    favoriteDropdownOpen && "opacity-100!",
                    isFavorite ? "text-warning-70" : "text-neutral-87"
                  )}
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" side="bottom" className="min-w-fit">
                {favoriteOptionsData ? (
                  favoriteOptionsData.map(
                    ({ name: favoriteOptionsDataName, icon: FavoriteOptionsDataIcon, ...item }, index) => (
                      // eslint-disable-next-line sonarjs/no-array-index-key
                      <DropdownMenu.Item
                        key={index}
                        leading={FavoriteOptionsDataIcon && { icon: FavoriteOptionsDataIcon }}
                        onSelect={item.handler}
                      >
                        {favoriteOptionsDataName}
                      </DropdownMenu.Item>
                    )
                  )
                ) : (
                  <>
                    <DropdownMenu.Item onSelect={() => onFavoriteChange?.(false)}>
                      <StarFillIcon
                        className={cn("size-5 shrink-0", isFavorite ? "text-warning-70" : "text-neutral-87")}
                      />
                      {isFavorite ? "Remove favorite" : "Add to favorite"}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item leading={{ icon: CopyIcon }} onSelect={onCopyLink}>
                      Copy link
                    </DropdownMenu.Item>
                    <DropdownMenu.Item leading={{ icon: ExternalLinkIcon }} onSelect={onOpenNewTab}>
                      Open in new tab
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          )}
        </Link>
      </Tooltip.Trigger>
      <Tooltip.Content theme="light" className={cn(openSidebar && "hidden")}>
        {name}
        <Tooltip.Arrow theme="light"></Tooltip.Arrow>
      </Tooltip.Content>
    </Tooltip.Root>
  );
};

export default SidebarBodyCategoryItem;
